import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import {
  type FamilyMember,
  type FamilyMemberInput,
  RELATIONSHIP_LABELS,
  RELATIONSHIP_ICONS,
} from "@/hooks/useFamilyMembers";
import type { Database } from "@/integrations/supabase/types";

type FamilyRelationship = Database["public"]["Enums"]["family_relationship"];

interface FamilyMemberDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  member?: FamilyMember | null;
  onSubmit: (data: FamilyMemberInput) => Promise<void>;
  isLoading?: boolean;
}

const RELATIONSHIPS: FamilyRelationship[] = [
  "self",
  "spouse",
  "child",
  "parent",
  "sibling",
  "other",
];

export function FamilyMemberDialog({
  open,
  onOpenChange,
  member,
  onSubmit,
  isLoading,
}: FamilyMemberDialogProps) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [relationship, setRelationship] = useState<FamilyRelationship>("other");
  const [birthDate, setBirthDate] = useState("");
  const [notes, setNotes] = useState("");

  const isEditing = !!member;

  // Reset form when dialog opens/closes or member changes
  useEffect(() => {
    if (open) {
      if (member) {
        setFirstName(member.first_name);
        setLastName(member.last_name);
        setRelationship(member.relationship);
        setBirthDate(member.birth_date || "");
        setNotes(member.notes || "");
      } else {
        setFirstName("");
        setLastName("");
        setRelationship("other");
        setBirthDate("");
        setNotes("");
      }
    }
  }, [open, member]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    await onSubmit({
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      relationship,
      birth_date: birthDate || null,
      notes: notes.trim() || null,
      avatar_url: member?.avatar_url || null,
    });

    onOpenChange(false);
  };

  const isValid = firstName.trim() && lastName.trim();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? "Modifier le membre" : "Ajouter un membre"}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? "Modifiez les informations de ce membre de la famille"
              : "Ajoutez un membre de votre famille pour gérer ses documents"}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="firstName">Prénom *</Label>
              <Input
                id="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Jean"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">Nom *</Label>
              <Input
                id="lastName"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Dupont"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="relationship">Lien de parenté</Label>
            <Select
              value={relationship}
              onValueChange={(v) => setRelationship(v as FamilyRelationship)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RELATIONSHIPS.map((rel) => (
                  <SelectItem key={rel} value={rel}>
                    <span className="flex items-center gap-2">
                      <span>{RELATIONSHIP_ICONS[rel]}</span>
                      <span>{RELATIONSHIP_LABELS[rel]}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="birthDate">Date de naissance</Label>
            <Input
              id="birthDate"
              type="date"
              value={birthDate}
              onChange={(e) => setBirthDate(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Informations complémentaires..."
              rows={2}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              Annuler
            </Button>
            <Button type="submit" disabled={!isValid || isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {isEditing ? "Modification..." : "Ajout..."}
                </>
              ) : isEditing ? (
                "Modifier"
              ) : (
                "Ajouter"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
